import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { APP_NAME } from "../config/branding";
import apiClient from "../utils/apiClient";

const BillingSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // New flow: Stripe Elements redirect params
  const redirectStatus = searchParams.get("redirect_status");
  const paymentIntent = searchParams.get("payment_intent");
  // Legacy flow: session_id from old hosted checkout
  const sessionId = searchParams.get("session_id");

  const [status, setStatus] = useState("loading"); // loading | success | error
  const pollCountRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    // New flow: check redirect_status from Stripe Elements
    if (redirectStatus === "succeeded" || paymentIntent) {
      pollSubscriptionStatus();
    } else if (sessionId) {
      // Legacy flow
      pollLegacyStatus();
    } else {
      setStatus("error");
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const pollSubscriptionStatus = async () => {
    if (pollCountRef.current >= 15) {
      setStatus("success");
      return;
    }

    // On first poll, try verify-payment to activate directly (webhook-independent)
    if (pollCountRef.current === 0 && paymentIntent) {
      try {
        const verifyRes = await apiClient.post("/billing/verify-payment", {
          payment_intent: paymentIntent,
        }, { skipErrorToast: true });
        if (verifyRes.data.verified) {
          setStatus("success");
          return;
        }
      } catch {
        // Fall through to polling
      }
    }

    try {
      const res = await apiClient.get("/billing/subscription-status", {
        skipErrorToast: true,
      });
      const subStatus = res.data.subscription_status;

      if (subStatus === "active") {
        setStatus("success");
        return;
      }

      // Keep polling
      pollCountRef.current += 1;
      timerRef.current = setTimeout(pollSubscriptionStatus, 2000);
    } catch {
      pollCountRef.current += 1;
      if (pollCountRef.current >= 3 && redirectStatus === "succeeded") {
        setStatus("success");
      } else {
        timerRef.current = setTimeout(pollSubscriptionStatus, 2000);
      }
    }
  };

  const pollLegacyStatus = async () => {
    if (pollCountRef.current >= 10) {
      setStatus("success");
      return;
    }

    try {
      const res = await apiClient.get(`/billing/checkout-status/${sessionId}`, {
        skipErrorToast: true,
      });
      if (res.data.payment_status === "paid" || res.data.status === "complete") {
        setStatus("success");
        return;
      }
      pollCountRef.current += 1;
      timerRef.current = setTimeout(pollLegacyStatus, 2000);
    } catch {
      pollCountRef.current += 1;
      if (pollCountRef.current >= 3) {
        setStatus("success");
      } else {
        timerRef.current = setTimeout(pollLegacyStatus, 2000);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border/40 sticky top-0 z-40 bg-background/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="font-heading font-semibold text-lg tracking-tight">
              {APP_NAME}
            </Link>
          </div>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-6">
          {status === "loading" && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-muted-foreground mx-auto" />
              <h1
                className="text-2xl font-semibold text-foreground"
                data-testid="billing-processing"
              >
                Processing your payment...
              </h1>
              <p className="text-sm text-muted-foreground">
                Please wait while we confirm your subscription.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1
                className="text-2xl font-semibold text-foreground"
                data-testid="billing-success"
              >
                Subscription Active
              </h1>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Your account has been upgraded. You now have access to all features included in your
                plan.
              </p>
              <Button
                onClick={() => navigate("/dashboard")}
                className="gap-2"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1
                className="text-2xl font-semibold text-foreground"
                data-testid="billing-error"
              >
                Something went wrong
              </h1>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                We could not verify your payment. If you were charged, your subscription will
                activate shortly.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <Button variant="outline" onClick={() => navigate("/pricing")}>
                  Back to Pricing
                </Button>
                <Button onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingSuccess;
